import React, { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Panel, DataTable, Fld, StatusPill, money, fmtDate, inputCls } from "./shared";
import { VAT_TYPES } from "./InvoiceForm";
import { ReportDownloads } from "./FinanceReports";

const today = () => new Date().toISOString().slice(0, 10);
const blank = (kind) => ({ kind, supplier: "", reference: "", date: today(), due_date: "", description: "", amount: "", vat_type: "vat12", shipment_number: "", notes: "" });
const VAT = Object.fromEntries(VAT_TYPES);

function PayableForm({ kind, onSaved }) {
  const [f, setF] = useState(blank(kind));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/payables", { ...f, amount: parseFloat(f.amount) || 0 });
      toast.success(`${data.number} recorded`); setF(blank(kind)); onSaved();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const field = (k, label, props = {}) => (
    <Fld label={label}><Input data-testid={`ap-${kind}-${k}`} value={f[k]} onChange={set(k)} className={inputCls} {...props} /></Fld>
  );
  return (
    <Panel title={kind === "quotation" ? "Record a quotation" : "Record a supplier invoice"}
           subtitle={kind === "quotation" ? "Quotes received from suppliers before you commit." : "Invoices received from suppliers that you need to pay."}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {field("supplier", "Supplier")}
        {field("reference", kind === "quotation" ? "Quotation #" : "Supplier invoice #")}
        {field("date", "Date", { type: "date" })}
        {field("due_date", kind === "quotation" ? "Valid until" : "Due date", { type: "date" })}
        {field("description", "Description")}
        {field("amount", "Amount (VAT inclusive)", { type: "number", min: "0" })}
        <Fld label="VAT">
          <Select value={f.vat_type} onValueChange={(v) => setF({ ...f, vat_type: v })}>
            <SelectTrigger data-testid={`ap-${kind}-vat`} className={inputCls}><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{VAT_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </Fld>
        {field("shipment_number", "Shipment # (optional)")}
      </div>
      <Button data-testid={`ap-${kind}-save`} onClick={save} disabled={busy || !f.supplier || !(parseFloat(f.amount) > 0)} className="mt-4 bg-blue-600 hover:bg-blue-500">
        {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Plus size={14} className="mr-2" />} Save
      </Button>
    </Panel>
  );
}

function RowAction({ r, onDone }) {
  const [amt, setAmt] = useState("");
  const act = async (path, body) => {
    try { await api.post(`/finance/payables/${r.id}/${path}`, body); toast.success("Updated"); setAmt(""); onDone(); }
    catch (e) { toast.error(formatError(e)); }
  };
  if (r.kind === "quotation") {
    return (
      <Select value={r.status} onValueChange={(v) => act("status", { status: v })}>
        <SelectTrigger data-testid={`ap-status-${r.id}`} className={`h-8 w-28 ${inputCls}`}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{["Open", "Accepted", "Declined"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (r.status === "Paid") return <span className="text-xs text-emerald-300">Fully paid</span>;
  return (
    <div className="flex gap-1.5">
      <Input data-testid={`ap-pay-amount-${r.id}`} type="number" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder={money(r.amount - r.amount_paid)} className={`h-8 w-28 text-right font-mono-ops ${inputCls}`} />
      <Button data-testid={`ap-pay-${r.id}`} size="sm" disabled={!(parseFloat(amt) > 0)} className="h-8 bg-emerald-600 hover:bg-emerald-500"
              onClick={() => act("payment", { amount: parseFloat(amt), date: today() })}>Pay</Button>
    </div>
  );
}

export default function PayablesTab({ kind }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/finance/payables").then((r) => setRows(r.data.filter((x) => x.kind === kind))).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  const cols = [
    { key: "number", label: "#", mono: true },
    { key: "supplier", label: "Supplier" },
    { key: "reference", label: "Reference", mono: true },
    { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
    { key: "due_date", label: kind === "quotation" ? "Valid until" : "Due", render: (r) => fmtDate(r.due_date) },
    { key: "vat_type", label: "VAT", render: (r) => VAT[r.vat_type] },
    { key: "amount", label: "Amount", right: true, render: (r) => money(r.amount) },
    { key: "input_vat", label: "Input VAT", right: true, render: (r) => money(r.input_vat) },
    ...(kind === "bill" ? [{ key: "amount_paid", label: "Paid", right: true, render: (r) => money(r.amount_paid) }] : []),
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "act", label: kind === "quotation" ? "Update" : "Record payment", render: (r) => <RowAction r={r} onDone={load} /> },
  ];
  return (
    <div className="space-y-6">
      <PayableForm kind={kind} onSaved={load} />
      <Panel title={kind === "quotation" ? "Quotations" : "Payables"} right={<ReportDownloads report="payables" />}>
        <DataTable testid={`ap-${kind}-table`} columns={cols} rows={rows} empty="Nothing recorded yet." />
      </Panel>
    </div>
  );
}
